import api, { getApiBase, getToken } from "./api";

export interface FileItem {
  id: number;
  file_name: string;
  object_name: string;
  file_size: number;
  file_type: string;
  mime_type: string;
  owner_id: number;
  permission_type: string;
  index_status: string;
  content_text: string | null;
  category: string | null;
  department_id?: number | null;
  department_name?: string | null;
  job_type_id: number | null;
  work_stage_id: number | null;
  project_id: number | null;
  data_permission_rule_id?: number | null;
  data_permission_rule_name?: string | null;
  data_permission_rule_ids?: number[];
  work_stage_name?: string | null;
  job_type_name?: string | null;
  project_name?: string | null;
  downloadable?: boolean;
  created_at: string;
  updated_at: string;
}

export interface FilePermission {
  id: number;
  file_id: number;
  permission_type: string;
  allowed_users: string | null;
  allowed_departments: string | null;
  allowed_job_titles: string | null;
}

export interface FileDetail extends FileItem {
  index_tree: string | null;
  asr_result: string | null;
  asr_status: string | null;
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

export async function listFiles(
  skip = 0,
  limit = 2000,
  filters?: { department_id?: number; job_type_id?: number; work_stage_id?: number; project_id?: number; category?: string; source?: "upload" | "subscription"; search?: string }
): Promise<FileItem[]> {
  const params = new URLSearchParams();
  params.set("skip", String(skip));
  params.set("limit", String(limit));
  if (filters?.department_id) params.set("department_id", String(filters.department_id));
  if (filters?.job_type_id) params.set("job_type_id", String(filters.job_type_id));
  if (filters?.project_id) params.set("project_id", String(filters.project_id));
  if (filters?.work_stage_id) params.set("work_stage_id", String(filters.work_stage_id));
  if (filters?.category) params.set("category", filters.category);
  if (filters?.source) params.set("source", filters.source);
  if (filters?.search) params.set("search", filters.search);
  return api.get<FileItem[]>(`/files?${params}`);
}

/** 服务端分页：用 fetch 直接拿 X-Total-Count 响应头 */
export async function listFilesPage(
  skip: number,
  limit: number,
  filters?: { source?: "upload" | "subscription"; search?: string; department_id?: number; job_type_id?: number; work_stage_id?: number; project_id?: number; category?: string }
): Promise<{ items: FileItem[]; total: number }> {
  const params = new URLSearchParams();
  params.set("skip", String(skip));
  params.set("limit", String(limit));
  if (filters?.source) params.set("source", filters.source);
  if (filters?.search) params.set("search", filters.search);
  if (filters?.department_id) params.set("department_id", String(filters.department_id));
  if (filters?.job_type_id) params.set("job_type_id", String(filters.job_type_id));
  if (filters?.project_id) params.set("project_id", String(filters.project_id));
  if (filters?.work_stage_id) params.set("work_stage_id", String(filters.work_stage_id));
  if (filters?.category) params.set("category", filters.category);
  const token = getToken();
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/files?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`请求失败 ${res.status}`);
  const items = (await res.json()) as FileItem[];
  const total = Number(res.headers.get("X-Total-Count") || items.length);
  return { items, total };
}

export interface FileCountStats {
  total: number;
  completed: number;
  processing: number;
  pending: number;
  failed: number;
}

export async function getFileCount(): Promise<FileCountStats> {
  return api.get<FileCountStats>("/files/count");
}

export async function uploadFile(
  file: File,
  permissionType = "public",
  dataPermissionRuleId?: number,
  extra?: {
    department_id?: number;
    job_type_id?: number;
    work_stage_id?: number;
    project_id?: number;
    category?: string;
    data_permission_rule_id?: number;
    data_permission_rule_ids?: number[];
    allowed_departments?: string;
    allowed_job_titles?: string;
    allowed_users?: string;
    downloadable?: boolean;
  }
): Promise<FileItem> {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  params.set("permission_type", permissionType);
  if (extra?.downloadable !== undefined) {
    params.set("downloadable", String(extra.downloadable));
  }
  if (extra?.data_permission_rule_ids && extra.data_permission_rule_ids.length > 0) {
    params.set("data_permission_rule_ids", extra.data_permission_rule_ids.join(","));
  } else if (dataPermissionRuleId) {
    params.set("data_permission_rule_id", String(dataPermissionRuleId));
  } else if (extra?.data_permission_rule_id) {
    params.set("data_permission_rule_id", String(extra.data_permission_rule_id));
  }
  if (extra?.department_id) params.set("department_id", String(extra.department_id));
  if (extra?.job_type_id) params.set("job_type_id", String(extra.job_type_id));
  if (extra?.work_stage_id) params.set("work_stage_id", String(extra.work_stage_id));
  if (extra?.project_id) params.set("project_id", String(extra.project_id));
  if (extra?.category) params.set("category", extra.category);
  if (extra?.allowed_departments) params.set("allowed_departments", extra.allowed_departments);
  if (extra?.allowed_job_titles) params.set("allowed_job_titles", extra.allowed_job_titles);
  if (extra?.allowed_users) params.set("allowed_users", extra.allowed_users);
  return api.upload<FileItem>(`/files/upload?${params}`, formData);
}

export async function deleteFile(fileId: number): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/files/${fileId}`);
}

export async function getDownloadUrl(fileId: number): Promise<{ url: string }> {
  return api.get<{ url: string }>(`/files/${fileId}/download`);
}

export async function downloadFileContent(
  fileId: number,
  fallbackName: string
): Promise<DownloadedFile> {
  const token = getToken();
  const apiBase = getApiBase();
  const response = await fetch(`${apiBase}/files/${fileId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error("下载失败");
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";
  const matched = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const filename = matched ? decodeURIComponent(matched[1]) : fallbackName;
  return { blob, filename };
}

export async function getFileDetail(fileId: number): Promise<FileDetail> {
  return api.get<FileDetail>(`/files/${fileId}`);
}

export async function getFilePermissions(fileId: number): Promise<FilePermission> {
  return api.get<FilePermission>(`/files/${fileId}/permissions`);
}

export async function updateFilePermissions(
  fileId: number,
  permissionType: string,
  opts?: {
    allowed_users?: string;
    allowed_departments?: string;
    allowed_job_titles?: string;
  }
): Promise<{ message: string }> {
  const params = new URLSearchParams();
  params.set("permission_type", permissionType);
  if (opts?.allowed_users) params.set("allowed_users", opts.allowed_users);
  if (opts?.allowed_departments) params.set("allowed_departments", opts.allowed_departments);
  if (opts?.allowed_job_titles) params.set("allowed_job_titles", opts.allowed_job_titles);
  return api.put<{ message: string }>(`/files/${fileId}/permissions?${params}`);
}

export async function reindexFile(fileId: number): Promise<{ message: string }> {
  return api.post<{ message: string }>(`/chat/index/${fileId}`);
}

export async function updateFileMetadata(
  fileId: number,
  data: {
    department_id?: number;
    job_type_id?: number;
    work_stage_id?: number;
    project_id?: number;
    category?: string;
    data_permission_rule_id?: number | null;
    data_permission_rule_ids?: number[] | null;
    downloadable?: boolean;
  }
): Promise<{ message: string }> {
  const params = new URLSearchParams();
  if (data.department_id) params.set("department_id", String(data.department_id));
  if (data.job_type_id) params.set("job_type_id", String(data.job_type_id));
  if (data.project_id) params.set("project_id", String(data.project_id));
  if (data.work_stage_id) params.set("work_stage_id", String(data.work_stage_id));
  if (data.category) params.set("category", data.category);
  if (data.downloadable !== undefined) params.set("downloadable", String(data.downloadable));
  // 多选优先：传 data_permission_rule_ids（空数组 → 空字符串清空）
  if (data.data_permission_rule_ids !== undefined) {
    params.set(
      "data_permission_rule_ids",
      (data.data_permission_rule_ids || []).join(","),
    );
  } else if (data.data_permission_rule_id !== undefined && data.data_permission_rule_id !== null) {
    params.set("data_permission_rule_id", String(data.data_permission_rule_id));
  } else if (data.data_permission_rule_id === null) {
    params.set("data_permission_rule_id", "0");
  }
  return api.put<{ message: string }>(`/files/${fileId}?${params}`);
}
